import React from 'react'
import { Filter, Search, X } from 'lucide-react'

const OFFICES = ['US Senate', 'US House', 'Downballot', 'Governor', 'Issue', 'Presidential', 'National']

const BOOL_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
]

const ALERT_BOOL_FILTER_FIELDS = [
  'delta_alerts',
  'admo_alerts',
  'admo_plus_alerts',
  'ctv_alerts',
  'dashboards',
  'digital_dashboards',
]

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA',
  'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT',
  'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
]

const YELLOW = '#eab308'
const inputStyle = {
  height: 36,
  fontSize: 13,
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: '0 10px',
  backgroundColor: '#fff',
  color: '#111827',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
}

const selectStyle = {
  ...inputStyle,
  minWidth: 0,
  cursor: 'pointer',
}

function formatFieldLabel(field) {
  const map = {
    delta_alerts: 'Delta',
    admo_alerts: 'AdMo',
    admo_plus_alerts: 'AdMo+',
    ctv_alerts: 'CTV',
    dashboards: 'Dash',
    digital_dashboards: 'Digital',
  }
  return map[field] || field
}

export default function AdImpactFilterBar({
  filters,
  setFilters,
  uniqueAEs,
  uniqueElections,
  uniqueYears,
  filteredCount,
}) {
  const hasFilters =
    filters.search ||
    filters.office ||
    filters.state ||
    filters.account_executive ||
    filters.election ||
    filters.year ||
    filters.delta_alerts !== 'all' ||
    filters.admo_alerts !== 'all' ||
    filters.admo_plus_alerts !== 'all' ||
    filters.ctv_alerts !== 'all' ||
    filters.dashboards !== 'all' ||
    filters.digital_dashboards !== 'all'

  const clearFilters = () => {
    setFilters({
      search: '',
      office: '',
      state: '',
      account_executive: '',
      election: '',
      year: '',
      delta_alerts: 'all',
      admo_alerts: 'all',
      admo_plus_alerts: 'all',
      ctv_alerts: 'all',
      dashboards: 'all',
      digital_dashboards: 'all',
    })
  }

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  const btnOutline = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    height: 32,
    padding: '0 10px',
    fontSize: 12,
    fontWeight: 500,
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    background: '#fff',
    color: '#6b7280',
    cursor: 'pointer',
    fontFamily: 'inherit',
  }

  return (
    <div
      style={{
        backgroundColor: '#fff',
        borderRadius: 12,
        border: '1px solid #e5e7eb',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#4b5563' }}>
          <Filter size={16} />
          <span>Filters</span>
          <span
            style={{
              backgroundColor: YELLOW,
              color: '#000',
              fontSize: 11,
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: 9999,
            }}
          >
            {filteredCount} clients
          </span>
        </div>
        {hasFilters && (
          <button type="button" style={btnOutline} onClick={clearFilters}>
            <X size={12} /> Clear Filters
          </button>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 200 }}>
          <Search
            size={16}
            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }}
          />
          <input
            type="text"
            placeholder="Search name or email..."
            value={filters.search}
            onChange={e => updateFilter('search', e.target.value)}
            style={{ ...inputStyle, width: '100%', paddingLeft: 36 }}
          />
        </div>

        <select
          value={filters.office || ''}
          onChange={e => updateFilter('office', e.target.value)}
          style={{ ...selectStyle, width: 140 }}
        >
          <option value="">All Offices</option>
          {OFFICES.map(o => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>

        <select
          value={filters.state || ''}
          onChange={e => updateFilter('state', e.target.value)}
          style={{ ...selectStyle, width: 110 }}
        >
          <option value="">All States</option>
          {US_STATES.map(s => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <select
          value={filters.account_executive || ''}
          onChange={e => updateFilter('account_executive', e.target.value)}
          style={{ ...selectStyle, width: 160 }}
        >
          <option value="">All AEs</option>
          {uniqueAEs.map(ae => (
            <option key={ae} value={ae}>
              {ae}
            </option>
          ))}
        </select>

        <select
          value={filters.election || ''}
          onChange={e => updateFilter('election', e.target.value)}
          style={{ ...selectStyle, width: 140 }}
        >
          <option value="">All Elections</option>
          {uniqueElections.map(e => (
            <option key={e} value={e}>
              {e}
            </option>
          ))}
        </select>

        <select
          value={filters.year || ''}
          onChange={e => updateFilter('year', e.target.value)}
          style={{ ...selectStyle, width: 110 }}
        >
          <option value="">All Years</option>
          {uniqueYears.map(y => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {ALERT_BOOL_FILTER_FIELDS.map(field => (
          <select
            key={field}
            value={filters[field]}
            onChange={e => updateFilter(field, e.target.value)}
            style={{ ...selectStyle, width: 150 }}
          >
            {BOOL_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {formatFieldLabel(field)}: {opt.label}
              </option>
            ))}
          </select>
        ))}
      </div>
    </div>
  )
}
